/**
 * The Community entry's pill state, shared by every surface on the page (the xl nav chip, the
 * header chip below xl, the drawer row, the phone home card) through ONE module store:
 *
 * - `activity`: `/api/community/activity`, read once per hard load after mount (one promise for
 *   every surface; re-read when a later mount finds it over a minute old);
 * - the member's unread count: the header bell's own figure, pushed in by `SiteHeader` through
 *   `HeaderAuth`'s `onUnreadChange` — never a second notifications poller, never `/me`
 *   (Constraint a8b375cd);
 * - `seen`: the reader's last arrival at /community on this device (`sg:community-pill-seen:v1`
 *   in localStorage — a per-device convenience, never sent to SG's server or DealHunt, whose
 *   activity figure stays the same for every reader). Read after mount; a second tab follows
 *   through the `storage` event. The rule itself is `communityPill`'s.
 *
 * The server snapshot is empty, so server HTML (shared-cached) and the hydration pass carry no
 * pill at all — never a reader's red count (Constraint cebb1e39: reader state renders after mount).
 * The seen mark is reader state too: it lives only in the client store, never in that snapshot.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { publicConfig } from 'config'
import type { CommunityActivity } from 'lib/community/types'
import {
  communityActivityOf,
  communityPill,
  communitySeenMarkOf,
  localDay,
  type CommunityPill,
  type CommunitySeenMark,
} from 'lib/community/entry-pill'

export const COMMUNITY_ACTIVITY_ENDPOINT = '/api/community/activity'
const ACTIVITY_MAX_AGE_MS = 60_000
export const COMMUNITY_PILL_SEEN_KEY = 'sg:community-pill-seen:v1'

interface PillState {
  activity: CommunityActivity | null
  unread: number
  capped: boolean
  seen: CommunitySeenMark | null
}

const EMPTY: PillState = { activity: null, unread: 0, capped: false, seen: null }

let state: PillState = EMPTY
let loadedAt = 0
let inFlight: Promise<void> | null = null
let seenLoaded = false
const listeners = new Set<() => void>()

function set(next: Partial<PillState>): void {
  const merged = { ...state, ...next }
  if (
    merged.activity === state.activity &&
    merged.unread === state.unread &&
    merged.capped === state.capped &&
    merged.seen === state.seen
  ) {
    return
  }
  state = merged
  listeners.forEach(fn => fn())
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

const snapshot = (): PillState => state
const serverSnapshot = (): PillState => EMPTY

function endpoint(): string {
  return publicConfig('useTrailingSlash')
    ? `${COMMUNITY_ACTIVITY_ENDPOINT}/`
    : COMMUNITY_ACTIVITY_ENDPOINT
}

/** One read for every mounted surface; a failure keeps what the page already shows. */
export function loadCommunityActivity(): Promise<void> {
  if (typeof fetch !== 'function') {
    return Promise.resolve()
  }
  if (loadedAt && Date.now() - loadedAt < ACTIVITY_MAX_AGE_MS) {
    return Promise.resolve()
  }
  inFlight ??= fetch(endpoint(), { headers: { Accept: 'application/json' } })
    .then(res => (res.ok ? (res.json() as Promise<{ activity?: unknown }>) : null))
    .then(body => {
      if (body) {
        loadedAt = Date.now()
        set({ activity: communityActivityOf(body.activity) })
      }
    })
    .catch(() => undefined)
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

/** The header bell's unread count (`HeaderAuth` `onUnreadChange`); 0 on sign-out. */
export function setCommunityUnread(unread: number, capped: boolean): void {
  set({ unread: Math.max(0, unread), capped: unread > 0 && capped })
}

// ── the seen mark (localStorage; private mode, blocked site data and quota all throw) ──────────

function readSeen(): CommunitySeenMark | null {
  try {
    const raw = window.localStorage.getItem(COMMUNITY_PILL_SEEN_KEY)
    return raw ? communitySeenMarkOf(JSON.parse(raw)) : null
  } catch {
    return null // unreadable → no mark: the pill behaves as it did before the rule
  }
}

function onStorage(event: StorageEvent): void {
  // `key` null = the other tab cleared storage
  if (event.key === COMMUNITY_PILL_SEEN_KEY || event.key === null) {
    set({ seen: readSeen() })
  }
}

/** Reads the mark once per page (client only) and follows other tabs' writes. */
export function loadCommunitySeen(): void {
  if (seenLoaded || typeof window === 'undefined') {
    return
  }
  seenLoaded = true
  set({ seen: readSeen() })
  try {
    window.addEventListener('storage', onStorage)
  } catch {
    // no storage events: this tab keeps its own mark
  }
}

/**
 * The reader is on /community: hide the day/week pill for the rest of the local day, and after
 * it until the figure differs from the one shown now (`activity` → its pill's count; no pill →
 * count null, so any later figure shows). A failed write still hides it for this page's life.
 */
export function markCommunitySeen(
  activity: CommunityActivity | null = state.activity,
  now = new Date()
): void {
  loadCommunitySeen() // attaches the other-tab listener if this is the page's first touch
  const shown = communityPill(activity, 0)
  const kind = shown?.kind === 'day' || shown?.kind === 'week' ? shown.kind : null
  const seen: CommunitySeenMark = {
    date: localDay(now),
    count: kind && shown ? Number(shown.count) : null,
    kind,
  }
  try {
    window.localStorage.setItem(COMMUNITY_PILL_SEEN_KEY, JSON.stringify(seen))
  } catch {
    // storage blocked or full: the in-memory mark below still hides the pill on this page
  }
  set({ seen })
}

/**
 * Every /community page (`CommunityShell`): mark seen on arrival with the figure the store holds
 * — the one the header showed. Only when the store had none yet (a hard load, the activity read
 * still in flight) mark again once that read resolves, so the stored count is the figure the
 * header gets for this visit; a store that already had a figure keeps it, even if a later
 * re-read brings a new one the reader never saw. No new network call: `loadCommunityActivity`
 * shares the page's one read.
 */
export function useMarkCommunitySeen(): void {
  useEffect(() => {
    const hadActivity = state.activity !== null
    markCommunitySeen()
    if (!hadActivity) {
      void loadCommunityActivity().then(() => markCommunitySeen())
    }
  }, [])
}

/** Test-only: forget the module state between specs. */
export function resetCommunityPillForTests(): void {
  state = EMPTY
  loadedAt = 0
  inFlight = null
  if (seenLoaded && typeof window !== 'undefined') {
    try {
      window.removeEventListener('storage', onStorage)
    } catch {
      // nothing attached
    }
  }
  seenLoaded = false
}

function pillOf(current: PillState, now: Date): CommunityPill | null {
  return communityPill(current.activity, current.unread, current.capped, {
    mark: current.seen,
    today: localDay(now),
  })
}

/** The pill the client store says to draw now (what `useCommunityPill` returns after mount). */
export function currentCommunityPill(now = new Date()): CommunityPill | null {
  return pillOf(state, now)
}

/** The pill to draw now — null on the server, during hydration and whenever there is none. */
export function useCommunityPill(): CommunityPill | null {
  const current = useSyncExternalStore(subscribe, snapshot, serverSnapshot)
  useEffect(() => {
    loadCommunitySeen()
    void loadCommunityActivity()
  }, [])
  return pillOf(current, new Date())
}
